// Quests — every quest this driver claimed, newest first, with where it stands.
//
// The Driver Quests page answers "which claims are in trouble" across all drivers; this
// answers "what has THIS driver done", so the three statuses the page splits into tabs
// are merged into one history here. The payout gap the page flags (completed, no payout
// record) is flagged the same way, in red, because this is where support looks first.
import { useCallback, useState } from 'react';
import { useFormatters } from '../../../hooks';
import DriverQuestClaimsApi from '../../../lib/api/DriverQuestClaimsApi';
import { Pagination, Stack, TintedStatusPill, Typography } from '../../../ui/core';
import { StarIcon } from '../../../ui/icons';
import { SectionCard, EmptyRows } from '../../../pages/DriverProfile/components';
import {
  Row,
  TrailingMeta,
  Meta,
  SectionSkeleton,
  SectionError,
  useSectionFetch,
} from '../../../pages/UserProfile/parts';
import {
  STATUS,
  claimKey,
  formatProgress,
  formatPayoutAmount,
  payoutStatusLabel,
} from '../../../pages/DriverQuests/claimFormatters';

const PAGE_SIZE = 5;

const STATUS_PILL = {
  [STATUS.ACTIVE]: { palette: 'accent', label: 'In progress' },
  [STATUS.COMPLETED]: { palette: 'success', label: 'Completed' },
  [STATUS.CANCELED]: { palette: null, label: 'Canceled' },
};

const byClaimedDesc = (a, b) => new Date(b.claimedAt) - new Date(a.claimedAt);

const QuestItem = ({ claim, first }) => {
  const f = useFormatters();
  const pill = STATUS_PILL[claim.status];

  let meta;
  if (claim.status === STATUS.ACTIVE) {
    meta = [claim.questType, `Progress ${formatProgress(claim)}`].filter(Boolean).join(' · ');
  } else if (claim.status === STATUS.COMPLETED) {
    meta = (
      <>
        Completed {f.date.toLocaleString(claim.completedAt, f.date.DateFormats.DATE_SHORT)} ·{' '}
        {formatPayoutAmount(claim.payoutAmountCents)} ·{' '}
        {/* A null payoutStatus is the payout-gap bug, never a routine "Pending". */}
        <Typography component="span" sx={{ fontSize: 'inherit', color: claim.payoutStatus ? 'inherit' : 'error.main' }}>
          {payoutStatusLabel(claim.payoutStatus)}
        </Typography>
      </>
    );
  } else {
    meta = `Canceled ${f.date.toLocaleString(claim.canceledAt, f.date.DateFormats.DATE_SHORT)}`;
  }

  return (
    <Row
      first={first}
      at={f.date.toLocaleString(claim.claimedAt, f.date.DateFormats.DATE_SHORT)}
      atMeta="Claimed"
      label={claim.questTitle || claim.questId}
      meta={meta}
      right={pill && <TintedStatusPill dense palette={pill.palette} label={pill.label} />}
    />
  );
};

const DriverQuestsCard = ({ user }) => {
  const [claims, setClaims] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [page, setPage] = useState(1);

  // The endpoint takes one status per call, so it is three calls merged here. One page
  // of 100 each: a driver claims one quest at a time, so a status past 100 is not a
  // history this card needs to page through — it says so instead.
  const load = useCallback(async () => {
    const pages = await Promise.all(
      Object.values(STATUS).map((status) =>
        DriverQuestClaimsApi.getClaims({ status, driverMongoId: user.id, limit: 100 }).then(
          ({ entries, nextCursor }) => ({ entries: entries.map((entry) => ({ ...entry, status })), nextCursor }),
        ),
      ),
    );
    // Filtered again on the client: an API that predates the `driverMongoId` filter
    // ignores the param and returns every driver's claims, and this card must never
    // show someone else's quests as this driver's.
    const mine = pages.flatMap((p) => p.entries).filter((entry) => String(entry.driverMongoId) === String(user.id));
    setClaims(mine.sort(byClaimedDesc));
    setTruncated(pages.some((p) => p.nextCursor));
    setPage(1);
  }, [user.id]);
  const [status, retry] = useSectionFetch(load);

  const pageCount = Math.ceil(claims.length / PAGE_SIZE);
  const visible = claims.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <SectionCard
      title="Quests"
      icon={<StarIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
      meta={
        status === 'done' && claims.length ? (
          <TrailingMeta>
            <Meta>
              {claims.length}
              {truncated ? '+' : ''} claimed
            </Meta>
          </TrailingMeta>
        ) : null
      }
    >
      {status === 'loading' && <SectionSkeleton />}
      {status === 'failed' && (
        <SectionError onRetry={retry}>Couldn&rsquo;t load this driver&rsquo;s quests.</SectionError>
      )}
      {status === 'done' && !claims.length && <EmptyRows>No quests claimed yet.</EmptyRows>}
      {status === 'done' &&
        visible.map((claim, idx) => <QuestItem key={claimKey(claim)} first={idx === 0} claim={claim} />)}
      {status === 'done' && pageCount > 1 && (
        <Stack alignItems="center" sx={{ py: 1 }}>
          <Pagination size="small" count={pageCount} page={page} onChange={(_, value) => setPage(value)} />
        </Stack>
      )}
    </SectionCard>
  );
};

export default DriverQuestsCard;
